import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { CSSTransition } from 'react-transition-group';
import {
    selectDiscussion,
    updateOnlineMembers,
    onClubhouseUpdate,
    resetClubhouse,
} from '../slices/clubhouse/clubhouse-slice';
import { fetchClubhouseData } from '../slices/clubhouse/fetch-clubhouse-data.thunk';
import { createDiscussion } from '../slices/clubhouse/create-discussion.thunk';
import { updateDiscussion } from '../slices/clubhouse/update-discussion.thunk';
import { AddPosterVote } from '../slices/clubhouse/add-poster-vote.thunk';
import { StartVoting } from '../slices/clubhouse/start-voting.thunk';
import { addToMessageList } from '../slices/messages/add-to-message-list.thunk';
import { updateMessageInList } from '../slices/messages/update-message-in-list.thunk';
import { loadDiscussionMessages } from '../slices/messages/load-discussion-messages.thunk';
import {
    ComparationType,
    loadDiscussionMessagesByLikes,
} from '../slices/messages/load-discussion-messages-by-likes.thunk';
import { sendMessage } from '../slices/messages/send-message.thunk';
import ChatArea from '../chat-area/chat-area';
import DiscussionList from '../disussion-list/discussion-list';
import './clubhouse-area.scss';
import * as signalR from '@microsoft/signalr';
import createNotification from '../../utils/createNotification';
import { Notification } from '../models/notification';
import { Discussion } from '../models/discussion';
import {
    setIsNewMessageReceived,
    UpdateState,
} from '../slices/messages/messages-slice';
import { checkForChanges } from '../slices/messages/check-for-changes.thunk';
import { refreshMessageList } from '../slices/messages/refresh-message-list.thunk';
import { Clubhouse } from '../models/clubhouse';
import Helpers from '../../utils/helper';
import { GroupType } from '../models/group-type';
import { CancelPosterVote } from '../slices/clubhouse/cancel-poster-vote.thunk';
import { RELOAD_COUNT } from '../../utils/constants';
import { likeMessage } from '../slices/messages/like-message.thunk';
import { addLikeToMessage } from '../slices/messages/add-like-to-message.thunk';
import { UpdateContentStatus } from '../slices/messages/update-content-status.thunk';
import { MessageReference } from '../models/message-reference';
import { refreshDirect } from '../../direct/slices/rooms/direct-rooms-slice';
import { updateMessage } from '../slices/messages/update-message.thunk';
import { useStateSelector } from '../../store/selectors';
import { addNotificationFlagToDelete } from '../../slices/notification-flags/notification-flags.slice';
import { RefreshNotificationFlags } from '../../slices/notification-flags/refresh-notification-flags';
import { PmgPocChatNotificationFlagsHelper } from '../../utils/notification-flags-helpers/pmgPocChatNotificationFlags.helper';
import { TokenHelper } from '../../utils/tokenHelper';
import { useSignalR } from '../../context/WebsocketProvider';
import { SendWebsocketMessage } from '../../utils/websocketHelper';
import {
    isViewAsBuyerActive,
    notifyViewAsBuyerReadOnly,
} from '../../utils/viewAsBuyer';
import { useLocation } from 'react-router-dom';
import {
    ClubhouseMemberMessagesAvailabilityPermission,
    ClubhouseMemberMessagesSendPermission,
} from '../models/clubhouse-member-permissions';
import { MessageStatus } from '../models/message-status';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import { messageReview } from '../slices/messages/message-review.thunk';
import { deleteMessageFromList } from '../slices/messages/delete-message-from-list.thunk';
import {
    ClubhouseActionFailed,
    ClubhouseActionType,
} from '../models/clubhouse-action-failed';
import { reportError } from '../../utils/errorReporting';
import { ClientErrorSource } from '../../models/ClientErrors/ClientErrorSource.enum';

interface ClubhouseAreaProps {
    clubhouseId: number;
    groupType: GroupType;
    isAdmin: boolean;
    isKbAvailable: boolean;
    isDataLoading: boolean;
    setIsDataLoading(value: boolean): void;
    isActive: boolean;
    setIsActive(value: boolean): void;
    setIsForbiddenError(value: boolean): void;
    setIsUnexpectedError(value: boolean): void;
    refreshClubhouseData(): void;
    messageReference: MessageReference;
    resetMessageReference: () => void;
    isDisabledMessageInput: boolean;
    isDisabledLikeButton?: boolean;
    displayTopLikedPosts?: () => void;
    preselectedDiscussionId?: number | null;
    customDiscussionsName?: string;
    isDisscussionDeleteIconVisible?: boolean;
    customNfPrefix?: string;
}

const ClubhouseArea = (props: ClubhouseAreaProps) => {
    const location = useLocation();
    const dispatch: any = useDispatch();
    const messages = useStateSelector(
        (state) => state.clubhouseMessages.discussions
    );
    const clubhouse = useStateSelector((state) => state.clubhouse.clubhouse);

    const isMessageLoading = useStateSelector(
        (state) => state.clubhouseMessages.isMessageLoading
    );
    const isNewMessageReceived = useStateSelector(
        (state) => state.clubhouseMessages.isNewMessageReceived
    );
    const isPageLoading = useStateSelector(
        (state) => state.clubhouseMessages.isPageLoading
    );
    const axios = useStateSelector((state) => state.core.axios);
    const connection = useSignalR().connection;
    const isConnected = useSignalR().isConnected;
    const [isHubConnected, setIsHubConnected] = useState(false);
    const [locationDiscussionIdWasSet, setLocationDiscussionIdWasSet] =
        useState(false);
    const sendFailureNonceRef = useRef(0);
    const [sendFailure, setSendFailure] = useState<{
        nonce: number;
        discussionId: number;
    } | null>(null);
    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const forbiddenStatusCode = 403;
    const pinnedDiscussions =
        props.groupType == GroupType.PMG
            ? ['General']
            : props.groupType == GroupType.IPOC
            ? ['Ecosystem Thoughts', 'IPOC Thoughts']
            : ['Ecosystem Thoughts', 'PLC Thoughts'];

    let currentDiscussionId = 0;
    const userId = TokenHelper.getUserIdFromToken();
    const [messageToReview, setMessageToReview] = useState<number>(null);
    const [isMessageToReviewApproved, setIsMessageToReviewApproved] = useState<
        boolean | null
    >(null);

    useEffect(() => {
        return () => {
            onDisconnect();
        };
    }, []);

    const onDisconnect = () => {
        if (!connection) {
            dispatch(resetClubhouse());
            return;
        }
        connection.off('CL_MessageReceived');
        connection.off('CL_LikeMessage');
        connection.off('CL_MessageUpdated');
        connection.off('CL_UpdateOnlineUsers');
        connection.off('CL_UpdateClubhouse');
        connection.off('CL_MessageApproved');
        connection.off('CL_MessageRejected');
        connection.off('CL_MessageDeleted');
        connection.off('CL_Disconnect');
        connection.off('CL_ClubhouseActionFailed');
        SendWebsocketMessage(connection, 'CL_DisconnectClubhouse', {
            clubhouseId: props.clubhouseId,
        });
        dispatch(resetClubhouse());
    };

    useEffect(() => {
        if (
            !locationDiscussionIdWasSet &&
            location.state?.discussionId &&
            clubhouse &&
            clubhouse.discussions?.some(
                (d) => d.id === location.state.discussionId
            )
        ) {
            setSelectedDiscussion(location.state.discussionId);
            setLocationDiscussionIdWasSet(true);
        }
    }, [clubhouse]);

    // 'idle' until the first successful group join, 'live' while handlers are
    // registered on a connected socket, 'resync' after a drop — the next join
    // must also reload whatever arrived while the socket was down.
    const socketStateRef = useRef<'idle' | 'live' | 'resync'>('idle');
    // The clubhouse this component currently shows; guards against a slow
    // response for a previously selected chat overwriting the current one.
    const activeClubhouseIdRef = useRef<number>(props.clubhouseId);
    // Monotonic id per loadClubhouseData call — a late response from an older
    // call must not win, even when it targeted the same clubhouse id.
    const loadNonceRef = useRef(0);

    useEffect(() => {
        // Arm the group-leave only once the loaded clubhouse actually belongs
        // to THIS chat. Right after a chat switch the previous chat's data is
        // still in the store for one render; a cleanup armed from that stale
        // snapshot would fire on the reset and wrongly leave the chat we just
        // started loading — and its resetClubhouse() would invalidate the
        // in-flight fetch, blanking the pane (#918 trace, 2026-08-06).
        if (
            clubhouse?.id === props.clubhouseId &&
            clubhouse?.currentMemberId
        ) {
            return () => {
                onDisconnect();
            };
        }
    }, [clubhouse?.id, clubhouse?.currentMemberId, props.clubhouseId]);

    // The clubhouse payload travels over plain HTTP — load it as soon as the
    // chat is known. Never gate the data on the websocket: a slow or failed
    // socket must degrade to "no live updates", not a blank chat.
    useEffect(() => {
        activeClubhouseIdRef.current = props.clubhouseId;
        if (props.clubhouseId) {
            loadClubhouseData();
        } else {
            props.setIsDataLoading(false);
        }
        return () => {
            // A response landing after this chat was left (unmount or chat
            // switch) must not write its selection into whatever is shown now.
            activeClubhouseIdRef.current = undefined;
        };
    }, [props.clubhouseId]);

    // Live updates: join the clubhouse group and register handlers once both
    // the socket and our membership are known. isConnected flips false→true on
    // every reconnect, and the server forgets group membership across
    // connections, so this effect also re-joins after a drop and re-syncs
    // missed data — replacing the onreconnected callbacks that used to
    // accumulate on the shared connection (one per fetch, never removable).
    useEffect(() => {
        if (!connection || !isConnected) {
            if (socketStateRef.current === 'live') {
                socketStateRef.current = 'resync';
            }
            return;
        }
        if (
            !clubhouse?.currentMemberId ||
            clubhouse?.id !== props.clubhouseId
        ) {
            // Either nothing is loaded yet, or the store still holds another
            // chat's data for one render after a switch — joining now would
            // register handlers against the wrong clubhouse.
            return;
        }
        startConection();
        if (socketStateRef.current === 'resync') {
            reloadClubhouse(clubhouse.selectedDiscussionId, connection);
        }
        socketStateRef.current = 'live';
    }, [connection, isConnected, clubhouse?.id, clubhouse?.currentMemberId, props.clubhouseId]);

    // #918 watchdog: a mounted chat with no loader and no error shown, sitting
    // either with no data at all or with data but no selected discussion, is
    // exactly the blank-screen bug. If that state persists, report it with
    // connection diagnostics so a prod occurrence carries details instead of
    // only a user screenshot. reportError dedups per session, so this cannot
    // spam the endpoint.
    useEffect(() => {
        const isDiscussionSelected = clubhouse?.selectedDiscussionId > 0;
        if (props.isDataLoading || (clubhouse?.id && isDiscussionSelected)) {
            return;
        }
        const timer = window.setTimeout(() => {
            reportError(
                clubhouse?.id
                    ? 'Clubhouse chat blank: loaded but no discussion selected'
                    : 'Clubhouse chat blank: no data, no loader, no error shown',
                ClientErrorSource.BlankScreenWatchdog,
                null,
                null,
                {
                    clubhouseId: props.clubhouseId ?? null,
                    discussionCount: clubhouse?.discussions?.length ?? null,
                    socketState: connection?.state ?? 'none',
                    isSocketConnected: isConnected,
                }
            );
        }, 10000);
        return () => window.clearTimeout(timer);
    }, [
        props.isDataLoading,
        clubhouse?.id,
        clubhouse?.selectedDiscussionId,
        connection,
        isConnected,
    ]);

    useEffect(() => {
        if (props.messageReference) {
            setSelectedDiscussion(props.messageReference.discussionId);
        }
    }, [props.messageReference]);

    // Any surfaced "unexpected error" in a chat is worth a diagnostics report,
    // same as the blank-screen watchdog. Deduped per session by the reporter.
    const reportUnexpectedError = (
        origin: string,
        httpStatus: number | null
    ) => {
        reportError(
            'Clubhouse fetch failed: unexpected error shown',
            ClientErrorSource.ClubhouseFetchFailure,
            null,
            null,
            {
                origin,
                httpStatus,
                clubhouseId: props.clubhouseId ?? null,
                socketState: connection?.state ?? 'none',
                isSocketConnected: isConnected,
            }
        );
    };

    const loadClubhouseData = () => {
        const requestedClubhouseId = props.clubhouseId;
        const loadNonce = ++loadNonceRef.current;
        props.setIsDataLoading(true);
        return dispatch(fetchClubhouseData(requestedClubhouseId)).then(
            (result: any) => {
                if (
                    requestedClubhouseId !== activeClubhouseIdRef.current ||
                    loadNonce !== loadNonceRef.current
                ) {
                    // A newer load owns the UI state now — either the chat
                    // changed, or the same chat was re-requested.
                    return;
                }
                if (result.payload?.data) {
                    props.setIsActive(result.payload.data.isActive);
                    const generalDiscusisonId =
                        result.payload.data.discussions.find((d: Discussion) =>
                            pinnedDiscussions.find((p) => d.name === p)
                        )?.id ?? result.payload.data.discussions[0]?.id;
                    setPreselectedDiscussion(
                        result.payload.data.discussions,
                        generalDiscusisonId
                    );
                } else if (
                    result.payload?.response?.status === forbiddenStatusCode
                ) {
                    props.setIsForbiddenError(true);
                } else {
                    reportUnexpectedError(
                        'initial-load',
                        result.payload?.response?.status ?? null
                    );
                    props.setIsUnexpectedError(true);
                }

                props.setIsDataLoading(false);
            }
        );
    };

    const setPreselectedDiscussion = (
        discussions: Array<Discussion>,
        generalDiscussionId: number
    ) => {
        if (
            props.preselectedDiscussionId &&
            discussions.find((d) => d.id == props.preselectedDiscussionId)
        ) {
            setSelectedDiscussion(props.preselectedDiscussionId);
        } else {
            setSelectedDiscussion(generalDiscussionId);
        }
    };

    const startConection = async () => {
        if (connection && isConnected) {
            try {
                setIsHubConnected(true);
                SendWebsocketMessage(connection, 'CL_ConnectClubhouse', {
                    clubhouseId: props.clubhouseId,
                });
                connection.off('CL_MessageReceived');
                connection.off('CL_LikeMessage');
                connection.off('CL_MessageUpdated');
                connection.off('CL_UpdateOnlineUsers');
                connection.off('CL_UpdateClubhouse');
                connection.off('CL_MessageApproved');
                connection.off('CL_MessageRejected');
                connection.off('CL_MessageDeleted');
                connection.off('CL_Disconnect');
                connection.off('CL_ClubhouseActionFailed');
                connection.on(
                    'CL_MessageReceived',
                    async (notification: Notification) => {
                        await handleMessageReceived(notification);
                    }
                );
                connection.on(
                    'CL_ClubhouseActionFailed',
                    (payload: ClubhouseActionFailed) => {
                        createNotification(
                            payload?.reason ||
                                'Your action could not be completed. Please try again.',
                            'error'
                        );
                        if (payload?.action === ClubhouseActionType.SendMessage) {
                            sendFailureNonceRef.current += 1;
                            setSendFailure({
                                nonce: sendFailureNonceRef.current,
                                discussionId: payload.discussionId,
                            });
                        }
                    }
                );

                connection.on(
                    'CL_LikeMessage',
                    (notification: Notification) => {
                        if (!doesUserSupportMessage(notification)) {
                            return;
                        }
                        dispatch(
                            addLikeToMessage({
                                clubhouseId: props.clubhouseId,
                                message: notification.message,
                                chatId: notification.chatId,
                            })
                        );
                    }
                );
                connection.on(
                    'CL_MessageUpdated',
                    (notification: Notification) => {
                        if (!doesUserSupportMessage(notification)) {
                            return;
                        }
                        dispatch(
                            updateMessageInList({
                                clubhouseId: props.clubhouseId,
                                message: notification.message,
                                chatId: notification.chatId,
                            })
                        );
                    }
                );
                connection.on(
                    'CL_UpdateOnlineUsers',
                    (onlineMembersIds: Array<number>) => {
                        sessionStorage.removeItem('reloadCount');
                        dispatch(updateOnlineMembers(onlineMembersIds));
                    }
                );
                connection.on(
                    'CL_UpdateClubhouse',
                    (notification: Clubhouse) => {
                        if (
                            notification.id &&
                            notification.id !== props.clubhouseId
                        ) {
                            dispatch(RefreshNotificationFlags());
                            return;
                        }
                        if (
                            currentDiscussionId &&
                            notification.discussions.filter(
                                (e) => e.id === currentDiscussionId
                            ).length === 0
                        ) {
                            setSelectedDiscussion(null);
                        }
                        if (
                            notification.members.some(
                                (m) =>
                                    m.userId === userId &&
                                    (!m.isActive ||
                                        (m.profile && !m.profile.isActive))
                            )
                        ) {
                            window.location.reload();
                        }
                        dispatch(onClubhouseUpdate(notification));
                        dispatch(UpdateState(notification));
                        currentDiscussionId = notification.selectedDiscussionId;
                    }
                );
                connection.on('CL_Disconnect', async () => {
                    if (!props.isAdmin) {
                        props.setIsForbiddenError(true);
                        SendWebsocketMessage(
                            connection,
                            'CL_DisconnectClubhouse',
                            {
                                clubhouseId: props.clubhouseId,
                            }
                        ); // ToDo 2.9: check if needed
                    }
                });
                connection.on(
                    'CL_MessageApproved',
                    (notification: Notification) => {
                        handleMessageReviewed(notification, true);
                    }
                );
                connection.on(
                    'CL_MessageDeleted',
                    (notification: Notification) => {
                        dispatch(
                            deleteMessageFromList({
                                clubhouseId: props.clubhouseId,
                                message: notification.message,
                                chatId: notification.chatId,
                            })
                        );
                    }
                );
                connection.on(
                    'CL_MessageRejected',
                    (notification: Notification) => {
                        handleMessageReviewed(notification, false);
                    }
                );
                dispatch(refreshDirect());
            } catch {
                if (isHubConnected) {
                    retryOrShowMessage(
                        'Unable to connect to Clubhouse. Please try again later.'
                    );
                }
            }
        }
    };

    const doesUserSupportMessage = (notification: Notification) => {
        const currentMember = getCurrentMember();
        const isPermissionPassed =
            currentMember.permissions.messagesAvailabilityPermission !==
                ClubhouseMemberMessagesAvailabilityPermission.ApprovedOnly ||
            notification.message.status === MessageStatus.Sent;

        const isClusterPassed =
            !notification.message.memberCluster ||
            currentMember.cluster === notification.message.memberCluster;

        return isPermissionPassed && isClusterPassed;
    };

    const canCurrentMemberSendMessages = () => {
        const currentMember = getCurrentMember();
        return (
            currentMember?.permissions.messagesSendPermission !==
            ClubhouseMemberMessagesSendPermission.Readonly
        );
    };

    const handleMessageReceived = async (notification: Notification) => {
        if (!doesUserSupportMessage(notification)) {
            return;
        }
        await dispatch(RefreshNotificationFlags());
        if (
            notification.clubhouseId &&
            notification.clubhouseId !== props.clubhouseId
        ) {
            return;
        }
        await dispatch(setIsNewMessageReceived(true));
        notification.message.sharedContents =
            notification.message.sharedContents.map((content) => {
                const modified = { ...content };
                modified.shouldBeUpdated = true;
                return modified;
            });
        await dispatch(
            addToMessageList({
                clubhouseId: props.clubhouseId,
                message: notification.message,
                chatId: notification.chatId,
            })
        );
    };

    const handleMessageReviewed = async (
        notification: Notification,
        isApproved: boolean
    ) => {
        if (!doesUserSupportMessage(notification)) {
            return;
        }
        await dispatch(RefreshNotificationFlags());
        if (
            notification.clubhouseId &&
            notification.clubhouseId !== props.clubhouseId
        ) {
            return;
        }
        if (isApproved) {
            await dispatch(setIsNewMessageReceived(true));
        }
        await dispatch(
            messageReview({
                clubhouseId: props.clubhouseId,
                message: notification.message,
                chatId: notification.chatId,
            })
        );
    };

    const getCurrentMember = () => {
        return clubhouse?.members.find((m) => m.userId === userId);
    };

    const retryOrShowMessage = (notificationText: string) => {
        let count = parseInt(sessionStorage.getItem('reloadCount'));

        if (isNaN(count)) {
            sessionStorage.setItem('reloadCount', String(0));
            count = 0;
        }

        if (count < RELOAD_COUNT) {
            sessionStorage.setItem('reloadCount', String(count + 1));
            createNotification('Reconnecting...', 'error');
            setTimeout(() => {
                // Own the clubhouse reload here — hosts' refreshClubhouseData
                // only refreshes page-level extras (comms hub tabs pass a no-op).
                loadClubhouseData();
                props.refreshClubhouseData();
            }, 1000);
        } else {
            sessionStorage.removeItem('reloadCount');
            createNotification(notificationText, 'error');
            reportUnexpectedError('reconnect-retries-exhausted', null);
            props.setIsUnexpectedError(true);
        }
    };

    const reloadClubhouse = (
        discussionId: number,
        connection: signalR.HubConnection
    ) => {
        dispatch(fetchClubhouseData(props.clubhouseId)).then((result: any) => {
            if (result.payload.data) {
                if (discussionId) {
                    dispatch(
                        checkForChanges({
                            clubhouseId: props.clubhouseId,
                            discussionId: discussionId,
                        })
                    );
                }
            } else if (
                result.payload?.response?.status === forbiddenStatusCode
            ) {
                props.setIsForbiddenError(true);
                SendWebsocketMessage(connection, 'CL_DisconnectClubhouse', {
                    clubhouseId: props.clubhouseId,
                });
            } else {
                reportUnexpectedError(
                    'reconnect-reload',
                    result.payload?.response?.status ?? null
                );
                props.setIsUnexpectedError(true);
                SendWebsocketMessage(connection, 'CL_DisconnectClubhouse', {
                    clubhouseId: props.clubhouseId,
                });
            }
        });
        dispatch(RefreshNotificationFlags());
    };

    const sendDiscussionMessage = (
        messageText: string,
        files: Array<number>,
        sharedContentIds: Array<number>
    ) => {
        if (isViewAsBuyerActive()) {
            notifyViewAsBuyerReadOnly();
            return false;
        }
        if (
            !connection ||
            !isConnected ||
            connection.state !== signalR.HubConnectionState.Connected
        ) {
            createNotification(
                'Connection is not available. Please try again.',
                'error'
            );
            return false;
        }
        const targetDiscussionId = clubhouse?.selectedDiscussionId;
        dispatch(
            sendMessage({
                messageText,
                clubhouseId: props.clubhouseId,
                selectedDiscussionId: targetDiscussionId,
                connection: connection,
                attachmentsIds: files,
                sharedContentIds: sharedContentIds,
            })
        )
            .unwrap()
            .catch(() => {
                // The message was not acknowledged by the server: either the socket
                // was not connected, or the frame never arrived (e.g. a half-open
                // socket where connection.state still reads Connected).
                // CL_ClubhouseActionFailed only fires for server-side rejections, so
                // this closes the "never arrived" gap -- notify the user and restore
                // the composer (via the same sendFailure signal) so nothing is lost.
                createNotification(
                    'Message not sent. Please try again.',
                    'error'
                );
                sendFailureNonceRef.current += 1;
                setSendFailure({
                    nonce: sendFailureNonceRef.current,
                    discussionId: targetDiscussionId,
                });
            });
        return true;
    };

    const likeDiscussionMessage = (messageid: number) => {
        if (isViewAsBuyerActive()) {
            notifyViewAsBuyerReadOnly();
            return false;
        }
        if (
            !connection ||
            !isConnected ||
            connection.state !== signalR.HubConnectionState.Connected
        ) {
            createNotification(
                'Connection is not available. Please try again.',
                'error'
            );
            return false;
        }
        dispatch(
            likeMessage({
                messageId: messageid,
                clubhouseId: props.clubhouseId,
                memberId: clubhouse?.currentMemberId,
                connection: connection,
                discussionId: clubhouse?.selectedDiscussionId,
            })
        );
        return true;
    };

    const setSelectedDiscussion = (id: number) => {
        if (clubhouse?.selectedDiscussionId !== id) {
            dispatch(selectDiscussion(id));
            dispatch(setIsNewMessageReceived(false));
            if (props.messageReference) {
                dispatch(
                    loadDiscussionMessages({
                        clubhouseId: props.clubhouseId,
                        discussionId: props.messageReference.discussionId,
                        isAscending: false,
                        messageId: props.messageReference.messageId,
                    })
                );
                return;
            }
            if (!messages[id]?.hasFirstPage) {
                dispatch(
                    refreshMessageList({
                        clubhouseId: props.clubhouseId,
                        discussionId: id,
                    })
                );
            }
        } else {
            if (props.messageReference) {
                dispatch(
                    loadDiscussionMessages({
                        clubhouseId: props.clubhouseId,
                        discussionId: clubhouse?.selectedDiscussionId,
                        isAscending: true,
                        messageId: props.messageReference.messageId,
                    })
                );
                return;
            }
        }
    };

    const getSeletedDiscussionName = () =>
        clubhouse?.discussions.find(
            (d: Discussion) => d.id === clubhouse?.selectedDiscussionId
        )?.name;

    const loadMessagesPage = (isAscending: boolean) => {
        if (
            (!isAscending &&
                !messages[clubhouse?.selectedDiscussionId].hasLastPage) ||
            (isAscending &&
                !messages[clubhouse?.selectedDiscussionId].hasFirstPage)
        ) {
            dispatch(
                loadDiscussionMessages({
                    clubhouseId: props.clubhouseId,
                    discussionId: clubhouse?.selectedDiscussionId,
                    isAscending: isAscending,
                    pageSize: null,
                })
            );
        }
    };

    const loadMessagesPageByLikes = (
        isAscending: boolean,
        numberOfLikes: number,
        comparationType: ComparationType
    ) => {
        if (
            (!isAscending &&
                !messages[clubhouse?.selectedDiscussionId].hasLastPage) ||
            (isAscending &&
                !messages[clubhouse?.selectedDiscussionId].hasFirstPage)
        ) {
            dispatch(
                loadDiscussionMessagesByLikes({
                    clubhouseId: props.clubhouseId,
                    discussionId: clubhouse?.selectedDiscussionId,
                    isAscending: isAscending,
                    pageSize: null,
                    numberOfLikes: numberOfLikes,
                    comparationType: comparationType,
                })
            );
        }
    };

    const showNewMessages = (): void =>
        dispatch(
            refreshMessageList({
                clubhouseId: props.clubhouseId,
                discussionId: clubhouse?.selectedDiscussionId,
            })
        );

    const createNewDiscussion = async (
        name: string,
        description: string
    ): Promise<any> =>
        dispatch(
            createDiscussion({
                clubhouseId: props.clubhouseId,
                name: name,
                description: description,
                isRemovable: true,
            })
        );

    const updateViewedDiscussion = async (
        discussionId: number,
        name: string,
        description: string
    ): Promise<any> =>
        dispatch(
            updateDiscussion({
                clubhouseId: props.clubhouseId,
                discussionId,
                name,
                description,
            })
        );

    const startDiscussionVoting = async (discussionId: number) => {
        dispatch(StartVoting({ clubhouseId: props.clubhouseId, discussionId }));
    };

    const posterVote = async (discussionId: number, vote: boolean) => {
        dispatch(
            AddPosterVote({
                clubhouseId: props.clubhouseId,
                discussionId,
                vote,
            })
        );
    };

    const cancelPosterVote = async (discussionId: number) => {
        dispatch(
            CancelPosterVote({
                clubhouseId: props.clubhouseId,
                discussionId,
            })
        );
    };

    const openAttachment = (attachmentId: number, messageId: number) => {
        axios
            .get(
                `/api/clubhouses/${props.clubhouseId}/discussions/${clubhouse?.selectedDiscussionId}/messages/${messageId}/attachments/${attachmentId}`
            )
            .then((response: any) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                } else {
                    createNotification(
                        'Unexpected error occurred while opening an attachment.',
                        'error'
                    );
                }
            });
    };

    const updateContent = (
        discussionId: number,
        messageId: number,
        contentId: number,
        isAddedToShelf: boolean
    ) => {
        dispatch(
            UpdateContentStatus({
                discussionId: discussionId,
                messageId: messageId,
                contentId: contentId,
                isAddedToShelf: isAddedToShelf,
            })
        );
    };

    const updateMsg = (message: string, messageId: number) => {
        if (
            !connection ||
            !isConnected ||
            connection.state !== signalR.HubConnectionState.Connected
        ) {
            createNotification(
                'Connection is not available. Please try again.',
                'error'
            );
            return false;
        }
        dispatch(
            updateMessage({
                connection: connection,
                clubhouseId: props.clubhouseId,
                messageText: message,
                messageId: messageId,
                selectedDiscussionId: clubhouse?.selectedDiscussionId,
            })
        );
        return true;
    };

    const deleteNotificationFlag = (discussionId: number) => {
        const removalFunction = async (discussionIdParam: number) => {
            const notificationFlagCode =
                PmgPocChatNotificationFlagsHelper.generateNotificationFlag(
                    props.groupType,
                    props.clubhouseId,
                    discussionIdParam,
                    props.customNfPrefix
                );
            const flag = notificationFlags.find(
                (f) =>
                    f.eventCode.toLowerCase() ==
                    notificationFlagCode.toLowerCase()
            );
            if (flag) {
                await dispatch(
                    addNotificationFlagToDelete({
                        ...flag,
                        refreshAfterRemoval: true,
                    })
                );
            }
        };
        removalFunction(discussionId);
    };

    const reviewMessage = (messageId: number, isApproved: boolean) => {
        SendWebsocketMessage(connection, 'CL_ReviewDraftMessage', {
            clubhouseId: props.clubhouseId,
            messageId: messageId,
            discussionId: clubhouse?.selectedDiscussionId,
            isApproved: isApproved,
        });
    };

    return (
        <div className="clubhouse-area ">
            {props.isDataLoading ? (
                <div className="loader">{Helpers.renderTableLoader()}</div>
            ) : (
                <div
                    className={cx(
                        'app-inner-layout chat-layout flex-container',
                        {
                            'open-mobile-menu': props.isActive,
                        }
                    )}>
                    <CSSTransition
                        classNames="TabsAnimation"
                        in={true}
                        appear={false}
                        enter={false}
                        exit={false}
                        timeout={0}>
                        <div className="app-inner-layout__wrapper chat-box-wrapper-main flex-container flex-row">
                            {clubhouse?.selectedDiscussionId > 0 ? (
                                <ChatArea
                                    openAttachment={openAttachment}
                                    isAdmin={props.isAdmin}
                                    authorizedMemberId={
                                        clubhouse?.currentMemberId
                                    }
                                    members={clubhouse?.members}
                                    messages={
                                        messages[
                                            clubhouse?.selectedDiscussionId
                                        ]?.messages
                                    }
                                    selectedDiscussionId={
                                        clubhouse?.selectedDiscussionId
                                    }
                                    clubhouseId={props.clubhouseId}
                                    groupType={props.groupType}
                                    loadMessagesPageByLikes={
                                        loadMessagesPageByLikes
                                    }
                                    discussionName={getSeletedDiscussionName()}
                                    sendMessage={sendDiscussionMessage}
                                    sendFailureSignal={sendFailure}
                                    loadMessagesPage={loadMessagesPage}
                                    isMessageLoading={isMessageLoading}
                                    isPageLoading={isPageLoading}
                                    showNewMessages={showNewMessages}
                                    isNewMessageReceived={isNewMessageReceived}
                                    hasFirstPage={
                                        messages[
                                            clubhouse?.selectedDiscussionId
                                        ]?.hasFirstPage
                                    }
                                    setIsNewMessageReceived={(e: any) =>
                                        dispatch(
                                            setIsNewMessageReceived(e.target)
                                        )
                                    }
                                    likeMessage={likeDiscussionMessage}
                                    isKbAvailable={props.isKbAvailable}
                                    updateContent={updateContent}
                                    messageReference={props.messageReference}
                                    isDisableMessageInput={
                                        props.isDisabledMessageInput ||
                                        !canCurrentMemberSendMessages()
                                    }
                                    isDisabledLikeButton={
                                        props.isDisabledLikeButton ||
                                        !canCurrentMemberSendMessages()
                                    }
                                    isDisableKbContent={!props.isKbAvailable}
                                    resetMessageReference={
                                        props.resetMessageReference
                                    }
                                    updateMessage={updateMsg}
                                    onReadNewMessages={() =>
                                        deleteNotificationFlag(
                                            clubhouse?.selectedDiscussionId
                                        )
                                    }
                                    reviewMessage={(messageId, isApproved) => {
                                        setMessageToReview(messageId);
                                        setIsMessageToReviewApproved(
                                            isApproved
                                        );
                                    }}
                                />
                            ) : (
                                []
                            )}
                            <DiscussionList
                                clubhouseId={props.clubhouseId}
                                displayTopLikedPosts={
                                    props.displayTopLikedPosts
                                }
                                groupType={props.groupType}
                                discussions={clubhouse?.discussions}
                                members={clubhouse?.members}
                                currentMemberId={clubhouse?.currentMemberId}
                                selectedDiscussionId={
                                    clubhouse?.selectedDiscussionId
                                }
                                selectDiscussion={setSelectedDiscussion}
                                pinnedDiscussions={pinnedDiscussions}
                                createDiscussion={createNewDiscussion}
                                updateDiscussion={updateViewedDiscussion}
                                startVoting={startDiscussionVoting}
                                posterVote={posterVote}
                                doesMemberHasViewOnlyAccessToPoc={
                                    props.isDisabledMessageInput
                                }
                                cancelPosterVote={cancelPosterVote}
                                customDiscussionsName={
                                    props.customDiscussionsName
                                }
                                isDisscussionDeleteIconVisible={
                                    props.isDisscussionDeleteIconVisible
                                }
                                customNfPrefix={props.customNfPrefix}
                            />
                        </div>
                    </CSSTransition>
                </div>
            )}

            {messageToReview ? (
                <ConfirmationModal
                    isVisible={messageToReview}
                    setIsVisible={() => {
                        setMessageToReview(null);
                        setIsMessageToReviewApproved(null);
                    }}
                    header={`Are you sure you want to ${
                        isMessageToReviewApproved ? 'approve' : 'reject'
                    } this message?`}
                    confirmationText={`This message will be ${
                        isMessageToReviewApproved ? 'posted' : 'unavailable'
                    } for Buyers in this chat.`}
                    onConfirm={() => {
                        reviewMessage(
                            messageToReview,
                            isMessageToReviewApproved
                        );
                    }}
                    params={undefined}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"></ConfirmationModal>
            ) : (
                []
            )}
        </div>
    );
};

export default ClubhouseArea;
